import type { PropsWithChildren, ReactNode } from 'react';
import { Badge } from '@ui/components/base/badges/badges';
import { Button } from '@ui/components/base/buttons/button';
import { LoadingIndicator } from '@ui/components/application/loading-indicator/loading-indicator';
import { Callout } from '@ui/console/primitives';
import { Quantity, formatCount } from '@ui/console/measures';
import { ConsolePage, SearchField } from '@ui/console/PageShell';
import { badgeColorFor } from '@ui/console/status';

export { ActionDialog, PageToolbar, SearchField, ToolbarActions } from '@ui/console/PageShell';
export type { PageFilters } from '@ui/console/PageShell';
import type { PageFilters } from '@ui/console/PageShell';

/**
 * Admin's screens all render through the shared console shell. `description`
 * and `action` are the older spellings of `purpose` and `actions`, kept so the
 * auth screens - which have no toolbar and no page verbs - did not have to be
 * touched to move the rest of the console onto one structure.
 */
export function Page({
  title,
  purpose,
  description,
  actions,
  action,
  toolbar,
  band,
  children,
}: PropsWithChildren<{
  title: string;
  purpose?: string;
  description?: string;
  actions?: ReactNode;
  action?: ReactNode;
  toolbar?: PageFilters;
  /** The screen's `MetricBand`. The shell decides where it goes. */
  band?: ReactNode;
}>) {
  const sentence = purpose ?? description;
  const verbs = actions ?? action;
  return (
    <ConsolePage
      title={title}
      {...(sentence ? { purpose: sentence } : {})}
      {...(verbs ? { actions: verbs } : {})}
      {...(toolbar ? { toolbar } : {})}
      {...(band ? { band } : {})}
    >
      {children}
    </ConsolePage>
  );
}

/**
 * A filter strip *inside* the content - AI costs narrows two of its sections
 * separately. The page's own strip is the shell's, via `Page`'s `toolbar`;
 * this one is deliberately not that, so it carries no search landmark and
 * cannot be mistaken for the page-level one.
 */
export function Filters({ children }: PropsWithChildren) {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-xl border border-secondary bg-primary p-3">
      {children}
    </div>
  );
}

/** A row of controls, trailing-aligned, as every console form ends with. */
export function Actions({ children }: PropsWithChildren) {
  return <div className="flex flex-wrap items-center justify-end gap-2">{children}</div>;
}

/**
 * Label/value pairs for a detail page, matching the console's record drawer.
 *
 * The label is a node rather than a string so it can carry its own in-place
 * explanation - a `Term` for the one technical word in it - without every
 * caller having to build its own definition list.
 */
export function DetailList({ items }: { items: Array<[ReactNode, ReactNode]> }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
      {items.map(([label, value], index) => (
        <div key={index} className="flex min-w-0 flex-col gap-0.5">
          <dt className="text-xs text-tertiary">{label}</dt>
          <dd className="m-0 min-w-0 text-sm font-medium text-secondary">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Admin's own lifecycle words are shown verbatim rather than humanised: several
 * screens and tests read the raw value, so only the rendering is shared with
 * the console, not the label text.
 */
export function Status({ value }: { value: string }) {
  return (
    <Badge type="pill-color" size="sm" color={badgeColorFor(value)}>
      {value}
    </Badge>
  );
}

export function State({
  loading,
  error,
  empty,
  onRetry,
  children,
}: PropsWithChildren<{
  loading: boolean;
  error?: string | undefined;
  empty?: boolean;
  onRetry: () => void;
}>) {
  if (loading)
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-tertiary" aria-live="polite">
        <LoadingIndicator size="sm" type="dot-circle" />
        Loading…
      </div>
    );
  if (error)
    return (
      <Callout
        tone="error"
        title={error}
        actions={
          <Button size="sm" color="secondary" onClick={onRetry}>
            Try again
          </Button>
        }
      />
    );
  if (empty)
    return (
      <div className="rounded-xl border border-secondary bg-primary p-6 text-sm text-tertiary">
        <p className="font-medium text-secondary">Nothing matches what you asked for.</p>
        <p className="pt-1">
          The read succeeded and came back with no rows, so this is an answer rather than a failure.
          Clear the search box and set any filter back to &ldquo;All&rdquo; to see everything.
        </p>
      </div>
    );
  return children;
}

/** The shared search box. Kept as a name here so screens need one import. */
export const Search = SearchField;

export function Pager({
  offset,
  limit,
  total,
  onPage,
}: {
  offset: number;
  limit: number;
  total: number;
  onPage: (offset: number) => void;
}) {
  return (
    <nav
      className="flex items-center justify-between gap-3 border-t border-secondary pt-3"
      aria-label="Pagination"
    >
      <span className="text-sm text-tertiary">
        Showing {total === 0 ? 0 : offset + 1}–{Math.min(total, offset + limit)} of{' '}
        <Quantity value={formatCount(total)} unit="rows" raw={total} />
      </span>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          color="secondary"
          isDisabled={offset === 0}
          onClick={() => onPage(Math.max(0, offset - limit))}
        >
          Previous
        </Button>
        <Button
          size="sm"
          color="secondary"
          isDisabled={offset + limit >= total}
          onClick={() => onPage(offset + limit)}
        >
          Next
        </Button>
      </div>
    </nav>
  );
}
